import Icon from "@imminently/decisively-ui/Icon";
import { useStore } from "react-redux";
import Button from "@imminently/decisively-ui/Button";
import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { global } from "./global";
import { toJpeg } from "html-to-image";
import pick from "lodash/pick";
import omit from "lodash/omit";
import { CircularBuffer } from "./lib/utils";
import Dialog, { useDialog, useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogControls } from "@/ui/dialogs/DialogHeader";
import Form, { FieldWatch } from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import TextArea from "@imminently/decisively-ui/TextArea";
import TextField from "@imminently/decisively-ui/TextField";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { useProject, useRelease, useWorkspace } from "./common";
import { useScope } from "./redux/scope";
import Breadcrumb from "@imminently/decisively-ui/Breadcrumb";
import { findConnectors } from "./pages/admin/Settings/settings.api";
import { getActiveTenancy } from "./features/tenancy";

export interface ConsoleLog {
  args: any[];
  timestamp: number;
  level: ConsoleMethod;
}

const ENABLE_IN_DEV = true;
const MAX_LOG_SIZE_KB = 5 * 1024; // 5MB for logs
const LOGS = new CircularBuffer<ConsoleLog>(100); // keep last 100 logs, but will be limited by size in the report
// @ts-ignore
window.__ERROR_REPORTING_LOGS = LOGS;

const CONSOLE_METHODS_TO_TRACK = ["error", "warn", "info", "log", "trace"] as const;
type ConsoleMethod = (typeof CONSOLE_METHODS_TO_TRACK)[number];

/**
 * Limits an array to a specified size in kilobytes.
 * Items are added to the result until adding the next item would exceed the size limit.
 * @param data - The array to limit
 * @param sizeInKb - Maximum size in kilobytes
 * @returns The truncated array that fits within the size limit
 */
export const limit = <T,>(data: Iterable<T>, sizeInKb: number): T[] => {
  const maxBytes = sizeInKb * 1024;
  let currentBytes = 0;
  const result: T[] = [];

  // DEBUG Calculate original data size
  // const originalSize = new Blob([JSON.stringify([...data])]).size;

  for (const item of data) {
    // Calculate the size of the item as JSON string
    const itemSize = new Blob([JSON.stringify(item)]).size;

    // Check if adding this item would exceed the limit
    if (currentBytes + itemSize > maxBytes) {
      break;
    }

    result.push(item);
    currentBytes += itemSize;
  }

  // DEBUG Calculate result size
  // const resultSize = new Blob([JSON.stringify(result)]).size;
  // console.log(`limit: original size: ${(originalSize / 1024).toFixed(2)} KB, result size: ${(resultSize / 1024).toFixed(2)} KB`);

  return result;
};

/**
 * Captures a screenshot of the application root element
 * @param selector - Optional CSS selector for the element to capture (defaults to "#root")
 * @returns A promise that resolves to a base64 encoded PNG image data URL, or undefined if capture fails
 */
export const takeScreenshot = async (
  element: HTMLElement = document.body
): Promise<string | undefined> => {
  try {
    // going with 0.5 quality jpeg to reduce size, compared to png its 1/3 the size
    const dataUrl = await toJpeg(element, {
      quality: 0.5,
      filter: (node) => {
        const tag = node.nodeName;
        // Skip non-rendering elements that are never part of the visual output.
        if (tag === "SCRIPT" || tag === "NOSCRIPT") return false;
        // Skip our own screenshot UI so it doesn't appear in the captured image.
        if ((node as HTMLElement).dataset?.screenshotUi) return false;
        const root = document.getElementById("root");
        if (!root?.contains(node)) {
          // Inside a shadow DOM (web component internals) — getRootNode() returns the
          // ShadowRoot, not the document. html-to-image dives into shadow roots via
          // nativeNode.shadowRoot, so we must block children here too.
          if (node.getRootNode() !== document) return false;
          // The web component host itself (e.g. <releases-panel>) — skip it and let
          // html-to-image never reach its shadow root.
          if ((node as HTMLElement).shadowRoot) return false;
        }
        return true;
      },
    });
    return dataUrl;
  } catch (error) {
    console.error("Failed to take screenshot:", error);
    return undefined;
  }
};

/**
 * Returns whether any event connector is configured to receive feedback.submitted events.
 * null means the check is still loading or couldn't be determined.
 */
export const useFeedbackConnectorCheck = (): boolean | null => {
  const [hasFeedbackConnector, setHasFeedbackConnector] = useState<boolean | null>(null);

  useEffect(() => {
    const tenancyId = getActiveTenancy();
    if (!tenancyId) {
      setHasFeedbackConnector(false);
      return;
    }
    findConnectors
      .submit({ params: { id: tenancyId } })
      .then((response) => {
        const connectors = response.data?.data ?? [];
        setHasFeedbackConnector(
          connectors.some((c) => c.subscribedEvents?.includes("feedback.submitted")),
        );
      })
      .catch(() => {
        setHasFeedbackConnector(null);
      });
  }, []);

  return hasFeedbackConnector;
};

export interface ErrorReportFormProps {
  onSubmit?: (description: string) => void;
  context?: FeedbackContextOptions;
}

interface ErrorReportFormValues {
  description: string;
}

const ErrorReportForm = (props: ErrorReportFormProps) => {
  const { context, onSubmit } = props;
  const { reportError } = useErrorReporting();
  const hasFeedbackConnector = useFeedbackConnectorCheck();
  const scope = useScope();
  const hasFullScope = Boolean(scope?.workspace && scope?.project && scope?.release);
  const [submit, setSubmit] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [takingScreenshot, setTakingScreenshot] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const selfRef = useRef<HTMLDivElement>(null);

  const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Failed to read image file"));
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    try {
      const imageDataUrl = await fileToDataUrl(file);
      setScreenshot(imageDataUrl);
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleFileSelection = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleImageUpload(file);
    // Allow selecting the same file again after removing it.
    event.target.value = "";
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await handleImageUpload(file);
  };

  const handleTakeScreenshot = async () => {
    setTakingScreenshot(true);

    // Temporarily hide this dialog so the screenshot captures what's behind it.
    // Walk up from our anchor ref to find [role="dialog"], then go one level higher
    // to include the overlay backdrop. Guard against hiding #root or body itself.
    const dialogEl = selfRef.current?.closest('[role="dialog"]') as HTMLElement | null;
    const portalEl = dialogEl?.parentElement as HTMLElement | null;
    const elementToHide =
      portalEl && portalEl.id !== "root" && portalEl.tagName !== "BODY" ? portalEl : dialogEl;

    try {
      if (elementToHide) {
        elementToHide.style.display = "none";
        // Double RAF ensures the browser has repainted before we capture.
        await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      }

      const image = await takeScreenshot();
      if (image) {
        setScreenshot(image);
      } else {
        toast.error("Failed to take screenshot");
      }
    } catch (error) {
      console.error("Failed to take screenshot:", error);
      toast.error("Failed to take screenshot");
    } finally {
      if (elementToHide) {
        elementToHide.style.display = "";
      }
      setTakingScreenshot(false);
    }
  };

  const handleOpenScreenshotInNewTab = async () => {
    if (!screenshot) return;
    // Browsers block navigation to data: URLs in new tabs, so convert to a blob URL first.
    const blob = await fetch(screenshot).then(r => r.blob());
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const handleDownloadScreenshot = () => {
    if (!screenshot) return;
    const link = document.createElement("a");
    link.href = screenshot;
    link.download = `screenshot-${Date.now()}.jpg`;
    link.click();
  };

  return (
    <Form
      defaultValues={{ description: "" }}
      onSubmit={async ({ description }) => {
        try {
          setSubmit(true);
          await reportError({ description, image: screenshot ?? undefined, sessionId: context?.sessionId });
          onSubmit?.(description);
        } catch (e: any) {
          if (e) {
            console.error(e);
            toast.error(e.message || "Failed to submit error report");
          }
          setSubmit(false);
        }
      }}
      className="contents"
    >
      <Dialog.Body asChild>
        <FormPanel>
          <div ref={selfRef} className="rounded border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            <div className="font-medium text-foreground">Included automatically</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Current page URL</li>
              <li>
                {hasFullScope
                  ? "Selected workspace, project, and release"
                  : "No workspace, project, or release is selected, so scope details are not included"}
              </li>
              <li>Recent console logs</li>
              <li>App context: auth/session, route, current release without rule graph, and current document when open</li>
              {context?.sessionId && <li>Current session record</li>}
            </ul>
          </div>
          {hasFeedbackConnector === false && (
            <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
              No event connector is configured to receive feedback. An admin can set one up in{" "}
              <strong>Settings &gt; Event Connectors</strong> by subscribing to the{" "}
              <strong>feedback.submitted</strong> event.
            </div>
          )}
          <TextField
            name="description"
            label="Description"
            inputComponent={TextArea}
            rows={6}
            fullWidth
          />
          {!screenshot ? (
            <div
              className={`flex h-37.5 w-full flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-[#ccc] px-4 transition-colors ${dragActive ? "bg-black/6" : takingScreenshot ? "bg-black/2" : ""}`}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (!dragActive) setDragActive(true);
              }}
              onDrop={handleDrop}
            >
              <Icon
                name="camera"
                size={32}
              />
              <div className="text-sm text-muted-foreground">Drag and drop an image here</div>
              <div className="text-xs text-muted-foreground">or choose an option</div>
              <div className="mt-1 flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  label="Upload image"
                  onClick={() => uploadInputRef.current?.click()}
                >
                  Upload image
                </Button>
                <Button
                  type="button"
                  size="sm"
                  label="Take screenshot"
                  onClick={handleTakeScreenshot}
                  disabled={takingScreenshot}
                >
                  Take screenshot
                </Button>
              </div>
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelection}
              />
            </div>
          ) : (
            <div className="relative flex max-h-75 items-center justify-center overflow-hidden rounded border border-[#ccc]">
              <div className="absolute right-2 top-2 z-[1] flex gap-1 rounded bg-white/90 p-0.5">
                <Button
                  icon
                  type="button"
                  size="sm"
                  label="Download"
                  onClick={handleDownloadScreenshot}
                  className="p-1"
                >
                  <Icon name="download" />
                </Button>
                <Button
                  icon
                  type="button"
                  size="sm"
                  label="Remove"
                  onClick={() => setScreenshot(null)}
                  className="p-1"
                >
                  <Icon name="delete" />
                </Button>
              </div>
              <a onClick={handleOpenScreenshotInNewTab} className="contents cursor-zoom-in">
                <img
                  src={screenshot}
                  alt="Screenshot preview"
                  className="block h-auto max-h-75 w-auto max-w-full object-contain"
                />
              </a>
            </div>
          )}
        </FormPanel>
      </Dialog.Body>
      <Dialog.Actions>
        <FieldWatch names={["description"]}>
          {({ description }) => (
            <Dialog.Actions.PrimaryButton
              label="Submit"
              type="submit"
              disabled={!description || submit}
              loading={submit}
            />
          )}
        </FieldWatch>
      </Dialog.Actions>
      {takingScreenshot && createPortal(
        <div
          data-screenshot-ui="true"
          className="pointer-events-none fixed inset-0 z-[99999] box-border border-4 border-amber-400 shadow-[0_0_0_1px_rgba(0,0,0,0.12),inset_0_0_0_1px_rgba(0,0,0,0.12)]"
        >
          <div className="absolute -top-1 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-b-2xl bg-amber-400 px-4 py-2 text-[0.8125rem] font-semibold text-black/75">
            <Icon name="camera" size={14} />
            Taking screenshot...
          </div>
        </div>,
        document.body,
      )}
    </Form>
  );
};

interface FeedbackContextOptions {
  sessionId?: string;
}

interface ErrorReportDialogProps {
  context?: FeedbackContextOptions;
}

const ErrorReportDialog = ({ context }: ErrorReportDialogProps) => {
  const dialogContext = useDialogContext();
  const scope = useScope();
  const hasFullScope = Boolean(scope?.workspace && scope?.project && scope?.release);
  const workspace = useWorkspace(scope?.workspace);
  const project = useProject(scope?.project);
  const release = useRelease(scope?.release);

  return (
    <Dialog.Content>
      <Dialog.Header>
        <Dialog.Title>Feedback</Dialog.Title>
        {hasFullScope && (
          <Breadcrumb className="text-muted-foreground">
            <Breadcrumb.List>
              <Breadcrumb.Item>
                {workspace?.name}
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                {project?.name}
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                Release {release?.releaseNo}
              </Breadcrumb.Item>
            </Breadcrumb.List>
          </Breadcrumb>
        )}
        <DialogControls feedback={false} />
      </Dialog.Header>
      <ErrorReportForm
        context={context}
        onSubmit={() => {
          dialogContext?.close();
          toast.info("Thank you for your feedback!");
        }}
      />
    </Dialog.Content>
  );
};

/** Pull from store directly, as the useFullScope hook might not be available at all times */
const getScopeFromStore = (store: any) => {
  const state = store.getState();
  const scope = state.scope;
  if (!scope?.workspace && !scope?.project && !scope?.release) {
    return null;
  }

  const workspace = scope.workspace ? state.resources?.workspaces?.data?.[scope.workspace] : null;
  const project = scope.project ? state.resources?.models?.data?.[scope.project] : null;
  const fullRelease = state.fullRelease?.value;
  const release = scope.release
    ? (fullRelease?.id === scope.release ? fullRelease : state.resources?.releases?.data?.[scope.release])
    : null;
  const scopeParts = [
    workspace?.name,
    project?.name,
    release?.releaseNo != null ? `Release ${release.releaseNo}` : null,
  ].filter(Boolean);

  return scopeParts.length > 0 ? scopeParts.join(" / ") : null;
};

const toJsonSafeValue = <T,>(value: T, fallback: T): T => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    console.error("Failed to serialise feedback context", error);
    return fallback;
  }
};

export interface ReportErrorOptions {
  error?: Error;
  description?: string;
  image?: string;
  sessionId?: string;
}

export const useErrorReporting = () => {
  const store = import.meta.env.NODE_ENV === "test" ? ({} as any) : useStore();
  const { pushDialog } = useDialog();
  return {
    open: (context?: FeedbackContextOptions) => {
      pushDialog({
        layer: WORKSPACE_LAYER,
        options: { intent: "form" },
        component: () => <ErrorReportDialog context={context} />,
      });
    },
    reportError: async (options?: ReportErrorOptions) => {
      // we have a 10MB limit, so we need to be careful about what we send
      const getStore = () => {
        try {
          const state = store.getState();
          // we want to limit this to only data we need
          const limitedState = pick(state, ["auth", "router", "scope"]); // example of picking only certain slices
          // remove rule graph from release as it could be massive. We can always go pull it ourselves
          const fullRelease = state.fullRelease?.value;
          const release =
            state.scope?.release && fullRelease?.id === state.scope.release
              ? omit(fullRelease, ["rule_graph"])
              : null;
          // only include the current document being viewed
          // don't use the word `document` as it might get confused with the global document
          let currentDocument = null as any;
          const docRef = state.documents?.document?.reference as string;
          if (docRef && state["documents-new"]) {
            // get the document by reference, but omit the graph as it could be huge. We can always go pull it ourselves if needed
            currentDocument = omit(state["documents-new"][docRef], ["document.graph"]);
          }
          const feedbackStore = {
            ...limitedState,
            ...(release ? { release } : {}),
            ...(currentDocument ? { document: currentDocument } : {}),
          };
          return toJsonSafeValue(feedbackStore, limitedState);
        } catch (error) {
          console.error("Failed to collect feedback app context", error);
          return {};
        }
      };

      // log the scope as text so we don't have to look it up
      let scope: string | null = null;
      try {
        scope = getScopeFromStore(store);
      } catch (error) {
        console.error("Failed to collect feedback scope", error);
      }
      // add it to the bottom of the description (top would override task title)
      const baseDescription = options?.description ?? options?.error?.stack;
      const description = [baseDescription, scope].filter(Boolean).join("\n\n");

      return await global.client("auth/reportError", {
        method: "POST",
        body: {
          url: window.location.href,
          // we may now be using the circular buffer, but we should still limit the logs to ensure we dont exceed the size limit
          logs: limit<ConsoleLog>(LOGS, MAX_LOG_SIZE_KB), // limit logs to 5MB
          image: options?.image, // this is optional and based on if the user takes a screenshot
          description: description,
          sessionId: options?.sessionId,
          store: getStore(),
        } as unknown as BodyInit,
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
  };
};

export const initErrorReporting = () => {
  if (import.meta.env.NODE_ENV === "test") {
    return;
  }

  if (import.meta.env.DEV && !ENABLE_IN_DEV) {
    return;
  }

  for (const method of CONSOLE_METHODS_TO_TRACK) {
    const original = console[method];
    console[method] = (...args: any[]) => {
      try {
        const res = JSON.stringify(args);
        // ensure the args are stringifiable, otherwise just log the original
        LOGS.push({ args, level: method, timestamp: Date.now() });
        // DEBUG Calculate result size
        // const resultSize = new Blob([JSON.stringify([...LOGS])]).size;
        // original.apply(console, [`logs size: ${LOGS.length} ${(resultSize / 1024).toFixed(2)} KB`]);
      } catch (e) {
        original.apply(console, args);
      }
      original.apply(console, args);
    };
  }
};
